import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { G } from './icons.tsx';
import type { ChatMessage } from './types.js';
import { useT } from '../i18n/I18n.tsx';

/**
 * Voice message playback.
 *
 * Play/pause, a scrubable waveform, and elapsed time — the interaction people
 * expect. The audio is encrypted at rest like any other attachment, so the
 * first press decrypts it; playback is ordinary `<audio>` after that.
 *
 * The waveform is generated from the message id rather than decoded from the
 * audio: decoding would mean pulling and decrypting every voice message just to
 * draw bars. It is deterministic, so a given message always looks the same.
 */

const BARS = 34;

function waveform(seed: string): number[] {
  // A small deterministic hash, so the shape is stable per message.
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  const bars: number[] = [];
  for (let index = 0; index < BARS; index += 1) {
    hash = (hash * 1103515245 + 12345) >>> 0;
    // Keep bars within a band that reads as speech rather than noise.
    bars.push(0.25 + ((hash >>> 16) % 100) / 100 / 1.45);
  }
  return bars;
}

export function VoiceMessage({
  message,
  onOpen,
}: {
  message: ChatMessage;
  onOpen: (message: ChatMessage) => Promise<{ url: string; mimeType: string }>;
}) {
  const t = useT();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState(0);

  const total = message.attachment?.durationSeconds ?? 0;
  const bars = useMemo(() => waveform(message.id), [message.id]);

  useEffect(
    () => () => {
      audioRef.current?.pause();
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  const toggle = useCallback(async () => {
    if (!url) {
      setBusy(true);
      try {
        const media = await onOpen(message);
        setUrl(media.url);
        // Play once the element has the source; the effect below starts it.
        setPlaying(true);
      } catch {
        setPlaying(false);
      } finally {
        setBusy(false);
      }
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
      setPlaying(true);
    } else {
      audio.pause();
      setPlaying(false);
    }
  }, [message, onOpen, url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && url && playing && audio.paused) void audio.play().catch(() => setPlaying(false));
  }, [url, playing]);

  const elapsed = playing || position > 0 ? position : total;
  const progress = total > 0 ? Math.min(1, position / total) : 0;

  return (
    <div className="voice">
      <button
        type="button"
        className="ibtn ibtn--sm"
        onClick={() => void toggle()}
        disabled={busy}
        aria-label={playing ? t('message.pauseVoice') : t('message.playVoice')}
      >
        <G n={busy ? 'clock' : playing ? 'pause' : 'play'} size={22} />
      </button>

      <div
        className="voice__wave"
        role="slider"
        tabIndex={0}
        aria-label={t('message.playbackPosition')}
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        aria-valuenow={Math.round(position)}
        onClick={(event) => {
          const audio = audioRef.current;
          if (!audio || !total) return;
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
          audio.currentTime = ratio * total;
          setPosition(audio.currentTime);
        }}
      >
        {bars.map((height, index) => (
          <span
            key={index}
            className={index / BARS <= progress ? 'voice__bar is-played' : 'voice__bar'}
            style={{ height: `${Math.round(height * 100)}%` }}
          />
        ))}
      </div>

      <span className="voice__time">
        {String(Math.floor(elapsed / 60)).padStart(1, '0')}:
        {String(Math.floor(elapsed % 60)).padStart(2, '0')}
      </span>

      {url ? (
        <audio
          ref={audioRef}
          src={url}
          onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
          onEnded={() => {
            setPlaying(false);
            setPosition(0);
          }}
          style={{ display: 'none' }}
        />
      ) : null}
    </div>
  );
}
